'use client';

import Link from '@eliadmin/core/src/components/link';

import { useEffect, useRef, useState } from 'react';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { CategoryCard } from './category-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { Button } from '../ui/button';

import { GoArrowDown, GoArrowUp } from 'react-icons/go';

export function CategoryHeader({
  category,
  showDescription = true,
}: {
  category: Category;
  showDescription?: boolean;
}) {
  const { t } = useTranslations();

  const descriptionRef = useRef<HTMLDivElement>(null);

  const [isClamped, setIsClamped] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = descriptionRef.current;
    if (element) {
      const isOverflow = element.scrollHeight > element.clientHeight + 1;
      setIsOverflowing(isOverflow);
    }
  }, [category.description]);

  const handleToggleClamp = () => {
    setIsClamped(prev => !prev);
  };

  return (
    <section
      className={cn(
        'custom-section',
        'gap-6 pb-12 pt-12 sm:gap-8 sm:pt-16 md:gap-10 lg:gap-12 lg:pt-20',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:items-end md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[820px]">
          <Link
            prefetch={false}
            href={ROUTES.HOME()}
            className="text-sm text-primary transition-all hover:text-primary/90 sm:text-base"
          >
            {t('text160')}
          </Link>
          <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">{category.name}</h1>

          {showDescription && (
            <div
              ref={descriptionRef}
              className={cn('ea-category-header__description w-full transition-all duration-300', {
                'line-clamp-5': isClamped,
              })}
            >
              <HtmlDescription
                color="foreground"
                content={category.description ?? ''}
                size="sm"
                className={cn('w-full', 'ea-category-header__description')}
              />
            </div>
          )}
        </div>
        {showDescription && isOverflowing && (
          <div
            onClick={handleToggleClamp}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm tracking-tight text-primary transition-all hover:opacity-90 sm:text-base"
          >
            <Button variant="alternative-default" size="icon">
              {isClamped ? (
                <GoArrowDown size={24} className="text-primary" />
              ) : (
                <GoArrowUp size={24} className="text-primary" />
              )}
            </Button>
            {isClamped ? t('text1360') : t('text604')}
          </div>
        )}
      </div>
      <div className={cn('custom-container', 'relative')}>
        <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 md:gap-1 lg:grid-cols-3 xl:gap-2">
          {!!category.sub_categories &&
            category.sub_categories.map(sub => (
              <CategoryCard
                category={sub as any}
                key={sub.slug}
                className={{
                  container: 'md:bg-[#FAFAFA] ',
                  button: 'md:bg-white md:group-hover:bg-[#FAFAFA]',
                }}
              />
            ))}
        </div>
      </div>
    </section>
  );
}
